// SPDX-License-Identifier: AGPL-3.0-or-later

import {MS_PER_DAY, MS_PER_HOUR} from '@fluxer/date_utils/src/DateConstants';
import {getSystemTimeZone} from '@fluxer/date_utils/src/DateIntrospection';
import {msg} from '@lingui/core/macro';

export const REMINDER_MIN_LEAD_MS = 60 * 1000;

export const REMINDER_PRESET_IDS = [
	'in_20m',
	'in_1h',
	'in_3h',
	'in_6h',
	'in_24h',
	'at_18',
	'at_21',
	'tomorrow_09',
	'next_week_09',
] as const;

export type ReminderPresetId = (typeof REMINDER_PRESET_IDS)[number];

export const REMIND_ME_DESCRIPTOR = msg({
	message: 'Remind me',
	comment: 'Message context menu item that schedules a reminder for this message.',
});
export const REMINDER_PRESET_IN_20M_DESCRIPTOR = msg({
	message: 'In 20 minutes',
	comment: 'Remind me submenu delay preset.',
});
export const REMINDER_PRESET_IN_1H_DESCRIPTOR = msg({
	message: 'In 1 hour',
	comment: 'Remind me submenu delay preset.',
});
export const REMINDER_PRESET_IN_3H_DESCRIPTOR = msg({
	message: 'In 3 hours',
	comment: 'Remind me submenu delay preset.',
});
export const REMINDER_PRESET_IN_6H_DESCRIPTOR = msg({
	message: 'In 6 hours',
	comment: 'Remind me submenu delay preset.',
});
export const REMINDER_PRESET_IN_24H_DESCRIPTOR = msg({
	message: 'In 24 hours',
	comment: 'Remind me submenu delay preset.',
});
export const REMINDER_CHOOSE_A_DATE_DESCRIPTOR = msg({
	message: 'Choose a date',
	comment: 'Remind me submenu item that opens a custom date and time picker.',
});
export const CLEAR_REMINDER_DESCRIPTOR = msg({
	message: 'Clear reminder',
	comment: 'Remind me submenu item that cancels a pending reminder on this message.',
});
export const REMINDER_PRESET_AT_18_DESCRIPTOR = msg({
	message: '6:00 PM',
	comment: 'Remind me submenu delay preset.',
});
export const REMINDER_PRESET_AT_21_DESCRIPTOR = msg({
	message: '9:00 PM',
	comment: 'Remind me submenu delay preset.',
});
export const REMINDER_PRESET_TOMORROW_09_DESCRIPTOR = msg({
	message: 'Tomorrow morning',
	comment: 'Remind me submenu delay preset.',
});
export const REMINDER_PRESET_NEXT_WEEK_09_DESCRIPTOR = msg({
	message: 'Next week',
	comment: 'Remind me submenu delay preset.',
});

export const REMINDER_PRESET_LABELS = {
	in_20m: REMINDER_PRESET_IN_20M_DESCRIPTOR,
	in_1h: REMINDER_PRESET_IN_1H_DESCRIPTOR,
	in_3h: REMINDER_PRESET_IN_3H_DESCRIPTOR,
	in_6h: REMINDER_PRESET_IN_6H_DESCRIPTOR,
	in_24h: REMINDER_PRESET_IN_24H_DESCRIPTOR,
	at_18: REMINDER_PRESET_AT_18_DESCRIPTOR,
	at_21: REMINDER_PRESET_AT_21_DESCRIPTOR,
	tomorrow_09: REMINDER_PRESET_TOMORROW_09_DESCRIPTOR,
	next_week_09: REMINDER_PRESET_NEXT_WEEK_09_DESCRIPTOR,
} as const;

interface ZonedParts {
	year: number;
	month: number;
	day: number;
	hour: number;
	minute: number;
	second: number;
}

function getZonedParts(date: Date, timeZone: string): ZonedParts {
	const values = new Map(
		new Intl.DateTimeFormat('en-US', {
			timeZone,
			year: 'numeric',
			month: '2-digit',
			day: '2-digit',
			hour: '2-digit',
			minute: '2-digit',
			second: '2-digit',
			hourCycle: 'h23',
		})
			.formatToParts(date)
			.map((part) => [part.type, part.value]),
	);
	return {
		year: Number(values.get('year')),
		month: Number(values.get('month')),
		day: Number(values.get('day')),
		hour: Number(values.get('hour')),
		minute: Number(values.get('minute')),
		second: Number(values.get('second')),
	};
}

function zonedWallTimeToUtc(
	timeZone: string,
	year: number,
	month: number,
	day: number,
	hour: number,
	minute: number,
	second = 0,
): Date {
	const wanted = Date.UTC(year, month - 1, day, hour, minute, second);
	let utc = wanted;
	for (let i = 0; i < 4; i++) {
		const parts = getZonedParts(new Date(utc), timeZone);
		const asUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
		utc += wanted - asUtc;
	}
	return new Date(utc);
}

function addCalendarDays(
	year: number,
	month: number,
	day: number,
	days: number,
): {year: number; month: number; day: number} {
	const date = new Date(Date.UTC(year, month - 1, day + days));
	return {year: date.getUTCFullYear(), month: date.getUTCMonth() + 1, day: date.getUTCDate()};
}

function nextLocalClockTime(hour: number, now: Date, timeZone: string): Date {
	const parts = getZonedParts(now, timeZone);
	let candidate = zonedWallTimeToUtc(timeZone, parts.year, parts.month, parts.day, hour, 0, 0);
	if (candidate.getTime() - now.getTime() < REMINDER_MIN_LEAD_MS) {
		const next = addCalendarDays(parts.year, parts.month, parts.day, 1);
		candidate = zonedWallTimeToUtc(timeZone, next.year, next.month, next.day, hour, 0, 0);
	}
	return candidate;
}

function localCalendarTime(daysFromToday: number, hour: number, now: Date, timeZone: string): Date {
	const parts = getZonedParts(now, timeZone);
	const next = addCalendarDays(parts.year, parts.month, parts.day, daysFromToday);
	return zonedWallTimeToUtc(timeZone, next.year, next.month, next.day, hour, 0, 0);
}

export function resolveReminderPreset(
	id: ReminderPresetId,
	now: Date = new Date(),
	timeZone: string = getSystemTimeZone(),
): Date {
	switch (id) {
		case 'in_20m':
			return new Date(now.getTime() + 20 * 60 * 1000);
		case 'in_1h':
			return new Date(now.getTime() + 60 * 60 * 1000);
		case 'in_3h':
			return new Date(now.getTime() + 3 * 60 * 60 * 1000);
		case 'in_6h':
			return new Date(now.getTime() + 6 * MS_PER_HOUR);
		case 'in_24h':
			return new Date(now.getTime() + MS_PER_DAY);
		case 'at_18':
			return nextLocalClockTime(18, now, timeZone);
		case 'at_21':
			return nextLocalClockTime(21, now, timeZone);
		case 'tomorrow_09':
			return localCalendarTime(1, 9, now, timeZone);
		case 'next_week_09':
			return localCalendarTime(7, 9, now, timeZone);
	}
}

export const REMINDER_TIME_STEP_MINUTES = 15;

export function reminderTimeSlotKey(hour: number, minute: number): string {
	return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
}

export function parseReminderTimeSlotKey(key: string): {hour: number; minute: number} {
	const [hourText, minuteText] = key.split(':');
	return {hour: Number(hourText), minute: Number(minuteText)};
}

export function listReminderTimeSlotKeys(): Array<string> {
	const keys: Array<string> = [];
	for (let hour = 0; hour < 24; hour++) {
		for (let minute = 0; minute < 60; minute += REMINDER_TIME_STEP_MINUTES) {
			keys.push(reminderTimeSlotKey(hour, minute));
		}
	}
	return keys;
}

export function resolveReminderWallTime(
	year: number,
	month: number,
	day: number,
	hour: number,
	minute: number,
	timeZone: string = getSystemTimeZone(),
): Date {
	return zonedWallTimeToUtc(timeZone, year, month, day, hour, minute, 0);
}

export function isReminderWallTimeValid(
	year: number,
	month: number,
	day: number,
	hour: number,
	minute: number,
	now: Date = new Date(),
	timeZone: string = getSystemTimeZone(),
): boolean {
	return (
		resolveReminderWallTime(year, month, day, hour, minute, timeZone).getTime() - now.getTime() >= REMINDER_MIN_LEAD_MS
	);
}

export function nextValidReminderTimeSlotKey(
	year: number,
	month: number,
	day: number,
	now: Date = new Date(),
	timeZone: string = getSystemTimeZone(),
): string | null {
	for (const key of listReminderTimeSlotKeys()) {
		const {hour, minute} = parseReminderTimeSlotKey(key);
		if (isReminderWallTimeValid(year, month, day, hour, minute, now, timeZone)) {
			return key;
		}
	}
	return null;
}

export interface ReminderPickerSelection {
	year: number;
	month: number;
	day: number;
	timeKey: string;
}

export function defaultReminderPickerSelection(
	now: Date = new Date(),
	timeZone: string = getSystemTimeZone(),
): ReminderPickerSelection {
	const parts = getZonedParts(now, timeZone);
	const todayKey = nextValidReminderTimeSlotKey(parts.year, parts.month, parts.day, now, timeZone);
	if (todayKey) {
		return {year: parts.year, month: parts.month, day: parts.day, timeKey: todayKey};
	}
	const tomorrow = addCalendarDays(parts.year, parts.month, parts.day, 1);
	return {
		year: tomorrow.year,
		month: tomorrow.month,
		day: tomorrow.day,
		timeKey:
			nextValidReminderTimeSlotKey(tomorrow.year, tomorrow.month, tomorrow.day, now, timeZone) ??
			reminderTimeSlotKey(0, 0),
	};
}
